import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import gustoAvatar from "@/assets/gusto-avatar.png";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = () => /Android/.test(navigator.userAgent);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const wasDismissed = sessionStorage.getItem("pwa-banner-dismissed");
    if (wasDismissed) { setDismissed(true); }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!wasDismissed) setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Show banner after 3s for iOS/browsers that don't fire the event
    const timer = setTimeout(() => {
      if (!isStandalone() && !wasDismissed) setShowBanner(true);
    }, 3000);

    return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(timer); };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === "accepted") {
        setShowBanner(false);
        setDismissed(true);
        sessionStorage.setItem("pwa-banner-dismissed", "1");
      }
    } else if (isIOS()) {
      setShowIOSGuide(true);
    } else {
      // Fallback: show generic instructions
      setShowIOSGuide(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    setShowIOSGuide(false);
    sessionStorage.setItem("pwa-banner-dismissed", "1");
  };

  if (isStandalone()) return null;

  return (
    <>
      {/* Full Banner */}
      <AnimatePresence>
        {showBanner && !dismissed && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:w-80"
          >
            <div className="bg-white rounded-2xl shadow-xl border p-4">
              <button onClick={handleDismiss} className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3">
                <img src={gustoAvatar} alt="Gusto" className="w-12 h-12 rounded-xl" />
                <div className="flex-1">
                  <p className="font-heading font-bold text-charcoal text-sm">Installer Gusto</p>
                  <p className="text-xs text-muted-foreground">Accédez à l'app depuis votre écran d'accueil</p>
                </div>
              </div>

              {/* iOS Guide */}
              {showIOSGuide && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 p-3 bg-blue-50 rounded-xl text-xs space-y-2">
                  {isIOS() ? (
                    <>
                      <p className="font-medium text-blue-800">Sur Safari :</p>
                      <div className="flex items-center gap-2 text-blue-700">
                        <span className="bg-blue-100 rounded p-1"><Share className="w-3.5 h-3.5" /></span>
                        <span>1. Appuyez sur le bouton <strong>Partager</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-blue-700">
                        <span className="bg-blue-100 rounded p-1"><Plus className="w-3.5 h-3.5" /></span>
                        <span>2. Choisissez <strong>"Sur l'écran d'accueil"</strong></span>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-blue-800">Dans votre navigateur :</p>
                      <p className="text-blue-700">Ouvrez le menu (3 points) puis choisissez <strong>"Installer l'application"</strong> ou <strong>"Ajouter à l'écran d'accueil"</strong></p>
                    </>
                  )}
                </motion.div>
              )}

              <Button onClick={handleInstall} className="w-full mt-3 rounded-full gap-2 h-10" data-testid="pwa-install-btn">
                <Download className="w-4 h-4" />
                {showIOSGuide ? "J'ai compris" : "Installer"}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Small persistent tab after dismiss */}
      {dismissed && !isStandalone() && (
        <button
          onClick={() => { setDismissed(false); setShowBanner(true); setShowIOSGuide(false); sessionStorage.removeItem("pwa-banner-dismissed"); }}
          className="fixed right-0 bottom-28 z-40 flex items-center gap-1.5
            bg-primary text-white pl-3 pr-2 py-2 rounded-l-full shadow-lg
            hover:pr-3 hover:shadow-xl transition-all group"
          data-testid="pwa-install-tab"
        >
          <Download className="w-3.5 h-3.5" />
          <span className="text-xs font-medium max-w-0 overflow-hidden group-hover:max-w-[100px] transition-all duration-300">Installer</span>
        </button>
      )}
    </>
  );
};

export default PWAInstallBanner;
